import { type FC, useEffect, useMemo } from "react"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { InAppInlineSlot } from "./InAppInlineSlot"
import {
  InAppBottomSlot,
  InAppModalSlot,
  InAppPanelSlot,
  InAppToastSlot,
} from "./InAppOverlaySlots"
import { readInAppLocalState, storeSeen } from "./inAppLocalState"
import {
  inAppMessagesActions,
  selectVisibleInAppMessages,
} from "./inAppMessagesSlice"
import { useMessageContext } from "./useMessageContext"
import { useOverlayTop } from "./useOverlayTop"

/**
 * The in-app messages consumer of one layout (LOCKER-483, R16): it keeps the audience context
 * in the store, loads dismissed / seen once, records first-seen marks, and renders the
 * overlay and inline slots. `InAppBannerSlot` renders the in-flow `top` / `below-header` slots.
 */
export const InAppMessagesConsumer: FC = () => {
  const dispatch = useAppDispatch()
  const context = useMessageContext()
  const visible = useAppSelector(selectVisibleInAppMessages)

  useEffect(() => {
    dispatch(inAppMessagesActions.localStateLoaded(readInAppLocalState()))
  }, [dispatch])

  useEffect(() => {
    dispatch(inAppMessagesActions.contextChanged(context))
  }, [dispatch, context])

  // First-seen marks feed `oncePerBrowser` (from the next visit on) and the feed unread count.
  const shownIds = useMemo(
    () =>
      [
        visible.top,
        visible["below-header"],
        visible.bottom,
        visible.toast,
        visible.modal,
        visible.panel,
        visible.inline,
      ]
        .flat()
        .map((v) => v.message.id)
        .sort()
        .join(","),
    [visible],
  )
  useEffect(() => {
    if (shownIds) storeSeen(shownIds.split(","), new Date().toISOString())
  }, [shownIds])

  const needsOverlayTop =
    visible.panel.length > 0 ||
    visible.toast.some(
      (v) => v.placement.type === "toast" && v.placement.corner.startsWith("top"),
    )
  const bannersKey = [...visible.top, ...visible["below-header"]]
    .map((v) => v.message.id)
    .join(",")
  const overlayTop = useOverlayTop(needsOverlayTop, bannersKey)

  return (
    <>
      <InAppBottomSlot visible={visible.bottom} />
      <InAppToastSlot visible={visible.toast} top={overlayTop} />
      <InAppModalSlot visible={visible.modal} />
      <InAppPanelSlot visible={visible.panel} top={overlayTop} />
      <InAppInlineSlot
        visible={visible.inline}
        surfacesKey={(context["page.uiSurfaces"] ?? []).join("|")}
      />
    </>
  )
}
